import i18next from 'i18next';
import { FilterCompTypes } from 'app/ComponentLibrary/Filter/utils';
import { getFilteredOptions, getOrderIdSearchLookups, dropDownFilterOptions, getFacilitySearchLookups } from '../helper';
import { totalCountNodes } from '@AMIEWEB/Common/TreeView/TreeViewHelpers';
import { candidateSearchFilterName } from '../Candidate/SearchUtils';
import { ISelection } from 'app/ComponentLibrary/Filter/CustomSelect';

export const PositionOpts = [
  {
    id: 0,
    value: { min: 0, max: 0 },
    name: '0',
    labelPrefix: i18next.t('search.globalSearch.order.filterNames.positions'),
  },
  {
    id: 1,
    value: { min: 1, max: 999 },
    name: '>0',
    labelPrefix: i18next.t('search.globalSearch.order.filterNames.positions'),
  },
  {
    id: 2,
    value: { min: 1, max: 10 },
    name: '1-10',
    labelPrefix: i18next.t('search.globalSearch.order.filterNames.positions'),
  },
  {
    id: 3,
    value: { min: 10, max: 50 },
    name: '10-50',
    labelPrefix: i18next.t('search.globalSearch.order.filterNames.positions'),
  },
  {
    id: 4,
    value: { min: 50, max: 99 },
    name: '50-99',
    labelPrefix: i18next.t('search.globalSearch.order.filterNames.positions'),
  },
  {
    id: 5,
    value: { min: 100, max: 999 },
    name: '>99',
    labelPrefix: i18next.t('search.globalSearch.order.filterNames.positions'),
  },
] as unknown as ISelection[];
import { getFilterRequest } from '../helper';

const countLeafChecked = (value: any): number => {
  if (!value || !Array.isArray(value?.skillsets)) return 0;
  let count = 0;
  const walk = (node: any) => {
    const hasChildren = Array.isArray(node?.children) && node.children.length > 0;
    const anyChildChecked = hasChildren && node.children.some((c: any) => c?.userChecked);
    if (node?.userChecked && !anyChildChecked) count += 1;
    if (hasChildren) node.children.forEach(walk);
  };
  value.skillsets.forEach(walk);
  return count;
};

export enum MarginFilterNames {
  marginType = 'marginType',
  orderId = 'orderId',
  payPackageStatus = 'payPackageStatus',
  jdhPostingStatus = 'jdhPostingStatus',
  division = 'division',
  facility = 'facility',
  skillSet = 'skillSet',
  discipline = 'discipline',
  states = 'states',
  targetMarginDefault = 'targetMarginDefault',
  shift = 'shift',
  positions = 'positions',
  targetMarginSource = 'targetMarginSource',
  defaultTargetMargin = 'defaultTargetMargin',
  defaultWeeklyPay = 'defaultWeeklyPay',
  payRangeMinMaxPercentage = 'payRangeMinMaxPercentage',
  actualTargetMargin = 'actualTargetMargin',
  weeklyGrossPay = 'weeklyGrossPay',
}

export class MarginFilterSpecs {
  constructor(public filterSpecs: any, public gridStateFilters: any) {}

  getMarginFilterSpecs = () => {
    return [
      {
        filterName: i18next.t('search.globalSearch.margin.filterNames.marginType'),
        name: MarginFilterNames.marginType,
        type: FilterCompTypes.SELECT,
        version2: true,
        isMultiSelect: false,
        nonNullable: true,
        options: [
          { id: 'Order', name: 'Order', value: 'Order' },
          { id: 'Division', name: 'Division', value: 'Division' },
          { id: 'Division Skill Set', name: 'Division Skill Set', value: 'Division Skill Set' },
          { id: 'Facility', name: 'Facility', value: 'Facility' },
        ].sort((a, b) => a.name.localeCompare(b.name)),
        selectAllValue: 'All',
        applyOnClickAway: true,
        applyOnEnter: true,
        hideClearAll: true,
        searchBoxHiddenWithSingleOption: true,
        hiddenInput: true,
      },
      {
        filterName: i18next.t('search.globalSearch.margin.filterNames.payPackageStatus'),
        name: MarginFilterNames.payPackageStatus,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        isMultiSelect: true,
        fetchOptions: async (keyword: string) => {
          return await getFilteredOptions(this.filterSpecs?.payPackageStatusOpts || [], keyword);
        },
        applyOnClickAway: true,
        applyOnEnter: true,
        isSelectAll: true,
        searchOnCharacterCount: 1,
      },
      {
        filterName: i18next.t('search.globalSearch.margin.filterNames.jdhPostingStatus'),
        name: MarginFilterNames.jdhPostingStatus,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        isMultiSelect: true,
        fetchOptions: async (keyword: string) => {
          return await getFilteredOptions(this.filterSpecs?.jdhPostingStatusOpts || [], keyword);
        },
        applyOnClickAway: true,
        applyOnEnter: true,
        isSelectAll: true,
        searchOnCharacterCount: 1,
      },
      {
        filterName: i18next.t('search.globalSearch.margin.filterNames.targetMarginSource'),
        name: MarginFilterNames.targetMarginSource,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        isMultiSelect: true,
        options: [
          { id: '1', name: 'Division', value: '1' },
          { id: '2', name: 'Division Skill Set', value: '2' },
          { id: '3', name: 'Facility', value: '3' },
        ],
        applyOnClickAway: true,
        applyOnEnter: true,
        isSelectAll: true,
      },
      {
        filterName: i18next.t('search.globalSearch.margin.filterNames.division'),
        name: MarginFilterNames.division,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        isMultiSelect: true,
        fetchOptions: async (keyword: string) => {
          return this.filterSpecs?.divisionOpts || [];
        },
        isSelectAll: true,
        applyOnClickAway: true,
        applyOnEnter: true,
        filterOptions: (options, { inputValue }) => {
          return dropDownFilterOptions(options, inputValue);
        },
      },
      {
        filterName: i18next.t('search.globalSearch.margin.filterNames.facility'),
        name: MarginFilterNames.facility,
        type: FilterCompTypes.FACILITYSEARCH,
        isMultiSelect: true,
        applyOnClickAway: true,
        applyOnEnter: true,
        isSelectAll: true,
      },
      {
        filterName: i18next.t('search.globalSearch.margin.filterNames.skillSet'),
        name: MarginFilterNames.skillSet,
        type: FilterCompTypes.CANDIDATESKILLSET,
        isMultiSelect: true,
        options: this.filterSpecs?.skillSetOpts || [],
        placeholder: 'Select',
        selectAllValue: 'All',
        version2: true,
        returnsObjectAsValue: true,
        size: 'large',
        isExact: true,
        customSelectedCount: (value: any) => countLeafChecked(value),
      },
      {
        filterName: i18next.t('search.globalSearch.margin.filterNames.orderId'),
        name: MarginFilterNames.orderId,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        isMultiSelect: true,
        fetchOptions: getOrderIdSearchLookups,
        applyOnClickAway: true,
        applyOnEnter: true,
        searchOnCharacterCount: 1,
      },
      {
        filterName: i18next.t('search.globalSearch.margin.filterNames.discipline'),
        name: candidateSearchFilterName.candidateSkillSet,
        type: FilterCompTypes.CANDIDATESKILLSET,
        isMultiSelect: true,
        options: [],
        placeholder: 'Select',
        selectAllValue: 'All',
        version2: true,
        returnsObjectAsValue: true,
        size: 'large',
        isExact: true,
        customSelectedCount: value => countLeafChecked(value),
        applyOnClickAway: true,
      },
      {
        filterName: i18next.t('search.globalSearch.margin.filterNames.positions'),
        name: MarginFilterNames.positions,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        isMultiSelect: false,
        returnsObjectAsValue: true,       
        options: PositionOpts,
        selectAllValue: 'All',
        applyOnClickAway: true,
      },
      {
        filterName: i18next.t('search.globalSearch.margin.filterNames.states'),
        name: MarginFilterNames.states,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        isMultiSelect: true,
        fetchOptions: async key => await getFilteredOptions(this.filterSpecs.stateOpts, key),
        isSelectAll: true,
        filterOptions: (options, { inputValue }) => {
          return dropDownFilterOptions(options, inputValue);
        },
        applyOnClickAway: true,
        applyOnEnter: true,
      },
      {
        filterName: i18next.t('search.globalSearch.margin.filterNames.targetMarginDefault'),
        name: MarginFilterNames.targetMarginDefault,
        type: FilterCompTypes.NUMBERRANGE,
        placeholder: 'Default Target Margin',
        numberRangeType: 'percentage',
        applyOnClickAway: true,
      },
      {
        filterName: i18next.t('search.globalSearch.margin.filterNames.shift'),
        name: MarginFilterNames.shift,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        isMultiSelect: true,
        fetchOptions: async (keyword: string) => {
          return await getFilteredOptions(this.filterSpecs?.shiftOpts || [], keyword);
        },
        applyOnClickAway: true,
        applyOnEnter: true,
        isSelectAll: true,
        searchOnCharacterCount: 1,
      },
      {
        filterName: i18next.t('search.globalSearch.margin.filterNames.defaultTargetMargin'),
        name: MarginFilterNames.defaultTargetMargin,
        type: FilterCompTypes.NUMBERRANGE,
        placeholder: 'Default Target Margin',
        numberRangeType: 'percentage',
        applyOnClickAway: true,
      },
      {
        filterName: i18next.t('search.globalSearch.margin.filterNames.defaultWeeklyPay'),
        name: MarginFilterNames.defaultWeeklyPay,
        type: FilterCompTypes.NUMBERRANGE,
        placeholder: 'Default Weekly Pay',
        numberRangeType: 'currency',
        applyOnClickAway: true,
      },
      {
        filterName: i18next.t('search.globalSearch.margin.filterNames.payRangeMinMaxPercentage'),
        name: MarginFilterNames.payRangeMinMaxPercentage,
        type: FilterCompTypes.NUMBERRANGE,
        placeholder: i18next.t('search.globalSearch.margin.filterNames.payRangeMinMaxPercentage'),
        numberRangeType: 'percentage',
        applyOnClickAway: true,
      },
      {
        filterName: i18next.t('search.globalSearch.margin.filterNames.actualTargetMargin'),
        name: MarginFilterNames.actualTargetMargin,
        type: FilterCompTypes.NUMBERRANGE,
        placeholder: 'Margin',
        numberRangeType: 'percentage',
        applyOnClickAway: true,
      },
      {
        filterName: i18next.t('search.globalSearch.margin.filterNames.weeklyGrossPay'),
        name: MarginFilterNames.weeklyGrossPay,
        type: FilterCompTypes.NUMBERRANGE,
        placeholder: 'Weekly Gross Pay',
        numberRangeType: 'currency',
        applyOnClickAway: true,
      },
    ];
  };
}

// Helper function to transform margin filter values to API request format
export const getMarginFilterRequest = (values: any, selectedChipFilters?: any) => {
  // Exclude division, jdhPostingStatus, facility, payPackageStatus, shift, skillSet, orderId, and number range filters from base filter request since we handle them specifically
  const { 
    division, 
    jdhPostingStatus, 
    facility,
    payPackageStatus,
    shift,
    skillSet,
    orderId,
    defaultTargetMargin, 
    defaultWeeklyPay, 
    actualTargetMargin, 
    weeklyGrossPay,
    ...valuesWithoutSpecialHandling 
  } = values;
  let filterRequest = getFilterRequest(valuesWithoutSpecialHandling, selectedChipFilters);

  // Map margin-specific filters to API format
  const marginFilters = {
    orderIds: (() => {
      const selectedOrderIds = (values[MarginFilterNames.orderId] || [])
        .map(item => item.value)
        .filter(orderId => orderId !== undefined && orderId !== null);
      return selectedOrderIds;
    })(),
    payPackageStatusIds: (() => {
      const selectedStatuses = (values[MarginFilterNames.payPackageStatus] || [])
        .map(item => item.value)
        .filter(status => status !== undefined && status !== null);
      return selectedStatuses;
    })(),
    jdhPostingStatusIds: (() => {
      const selectedStatuses = (values[MarginFilterNames.jdhPostingStatus] || [])
        .map(item => item.value)
        .filter(status => status !== undefined && status !== null);
      return selectedStatuses;
    })(),
    divisionIds: (() => {
      const selectedDivisions = (values[MarginFilterNames.division] || [])
        .map(item => item.value)
        .filter(division => division !== undefined && division !== null);
      return selectedDivisions;
    })(),
    facilityIds: (() => {
      const facilityValue = values[MarginFilterNames.facility];
      // Handle FACILITYSEARCH filter structure - it returns an object with selections property
      const selectedFacilities = facilityValue?.selections 
        ? facilityValue.selections.map(item => item.value || item.id)
        : Array.isArray(facilityValue) 
          ? facilityValue.map(item => item.value || item.id)
          : [];
      
      return selectedFacilities.filter(facility => facility !== undefined && facility !== null);
    })(),
    orderSkillsetFilter: (() => {
      const skillSetValue = values[MarginFilterNames.skillSet];
      if (!skillSetValue || !skillSetValue.skillsets || skillSetValue.skillsets.length === 0) {
        return {
          allowableMismatch: false,
          isExactMatch: false,
          operation: 'Or',
          skillsets: []
        };
      }

      // Find all checked skillsets recursively
      const getCheckedSkillsets = (skillsets) => {
        const checkedSkillsets = [];
        skillsets.forEach(skill => {
          if (skill.userChecked) {
            // Parse the value field (e.g., "1-62-68" -> disciplineId: "1", specialtyId: 62, subSpecialtyId: 68)
            const valueParts = skill.value.split('-');
            checkedSkillsets.push({
              disciplineId: valueParts[0],
              specialtyId: parseInt(valueParts[1]),
              subSpecialtyId: parseInt(valueParts[2]),
              filterName: 'skillsetFilter'
            });
          }
          if (skill.children && skill.children.length > 0) {
            checkedSkillsets.push(...getCheckedSkillsets(skill.children));
          }
        });
        return checkedSkillsets;
      };

      const checkedSkillsets = getCheckedSkillsets(skillSetValue.skillsets);

      return {
        allowableMismatch: skillSetValue.allowableMismatch || false,
        isExactMatch: skillSetValue.isExactMatch || false,
        operation: skillSetValue.operation || 'Or',
        skillsets: checkedSkillsets
      };
    })(),
    shiftIds: (() => {
      const selectedShifts = (values[MarginFilterNames.shift] || [])
        .map(item => item.value)
        .filter(shift => shift !== undefined && shift !== null);
      return selectedShifts;
    })(),
    positions: (() => {
      const selectedPositions = values[MarginFilterNames.positions]?.value || { min: 1, max: 999 };

      return selectedPositions;
    })(),
    defaultTargetMargin: (() => {
      const selectedMargin = values[MarginFilterNames.defaultTargetMargin];
      return selectedMargin ? { min: selectedMargin.min, max: selectedMargin.max } : undefined;
    })(),
    defaultWeeklyPay: (() => {
      const selectedPay = values[MarginFilterNames.defaultWeeklyPay];
      return selectedPay ? { min: selectedPay.min, max: selectedPay.max } : undefined;
    })(),
    actualTargetMargin: (() => {
      const selectedMargin = values[MarginFilterNames.actualTargetMargin];
      return selectedMargin ? { min: selectedMargin.min, max: selectedMargin.max } : undefined;
    })(),
    weeklyGrossPay: (() => {
      const selectedPay = values[MarginFilterNames.weeklyGrossPay];
      return selectedPay ? { min: selectedPay.min, max: selectedPay.max } : undefined;
    })(),
  };

  return {
    ...filterRequest,
    ...marginFilters,
  };
};
